// @mui
import Box, { BoxProps } from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, keyframes } from '@mui/material/styles';
// components
import Logo from '../logo';

// ----------------------------------------------------------------------

type LoadingScreenProps = BoxProps & {
  message?: string;
  fullScreen?: boolean;
  compact?: boolean;
};

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slide = keyframes`
  0% { transform: translateX(-120%); }
  50% { transform: translateX(30%); }
  100% { transform: translateX(220%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

export default function LoadingScreen({
  sx,
  message = 'Chargement en cours...',
  fullScreen = false,
  compact = false,
  ...other
}: LoadingScreenProps) {
  return (
    <Box
      sx={{
        px: compact ? 2 : 5,
        width: 1,
        flexGrow: 1,
        minHeight: compact ? 180 : 'min(480px, 70vh)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: fullScreen ? 'fixed' : 'relative',
        inset: fullScreen ? 0 : 'auto',
        zIndex: fullScreen ? (theme) => theme.zIndex.modal + 2 : 'auto',
        overflow: 'hidden',
        bgcolor: 'background.default',
        background: (theme) => {
          const primaryLight = theme.palette.primary.lighter || theme.palette.primary.light;
          const infoLight = theme.palette.info.lighter || theme.palette.info.light;

          return `linear-gradient(135deg, ${alpha(primaryLight, 0.38)} 0%, ${
            theme.palette.background.default
          } 46%, ${alpha(infoLight, 0.32)} 100%)`;
        },
        ...sx,
      }}
      role="status"
      aria-live="polite"
      aria-busy="true"
      {...other}
    >
      <Stack alignItems="center" spacing={2.5} sx={{ width: 1, maxWidth: 360 }}>
        <Box
          sx={{
            width: compact ? 76 : 96,
            height: compact ? 76 : 96,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: `${float} 2.4s ease-in-out infinite`,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: -6,
              borderRadius: 3,
              background: (theme) =>
                `conic-gradient(from 90deg, ${alpha(theme.palette.primary.main, 0)}, ${
                  theme.palette.primary.main
                }, ${theme.palette.info.main}, ${theme.palette.warning.main}, ${alpha(
                  theme.palette.primary.main,
                  0
                )})`,
              animation: `${rotate} 1.8s linear infinite`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 2.5,
              bgcolor: 'background.paper',
              boxShadow: (theme) =>
                `0 24px 60px ${alpha(
                  theme.palette.primary.darker || theme.palette.primary.dark,
                  0.16
                )}`,
            }}
          />
          <Logo
            disabledLink
            sx={{ width: compact ? 44 : 56, height: compact ? 44 : 56, zIndex: 1 }}
          />
        </Box>

        <Stack direction="row" spacing={1} aria-hidden="true">
          {[0, 1, 2].map((item) => (
            <Box
              key={item}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: item === 1 ? 'info.main' : item === 2 ? 'warning.main' : 'primary.main',
                animation: `${pulse} 1.1s ease-in-out infinite`,
                animationDelay: `${item * 0.16}s`,
              }}
            />
          ))}
        </Stack>

        <Box sx={{ width: 1, overflow: 'hidden', borderRadius: 999 }}>
          <LinearProgress
            variant="determinate"
            value={100}
            sx={{
              height: 8,
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.16),
              '& .MuiLinearProgress-bar': {
                width: '48%',
                background: (theme) =>
                  `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.info.main}, ${theme.palette.warning.main})`,
                animation: `${slide} 1.25s ease-in-out infinite`,
              },
            }}
          />
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {message}
        </Typography>
      </Stack>
    </Box>
  );
}
